/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Palette, ChevronRight } from 'lucide-react';
import FileUpload from './FileUpload';
import { DAISY_THEMES } from '../utils/constants';

const ThemeSwatch = ({ theme, isActive }) => (
  <div
    data-theme={theme}
    className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative"
  >
    <div className="bg-base-content w-1 h-1 rounded-full" />
    <div className="bg-primary w-1 h-1 rounded-full" />
    <div className="bg-secondary w-1 h-1 rounded-full" />
    <div className="bg-accent w-1 h-1 rounded-full" />
    {isActive && (
      <div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center">
        <svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    )}
  </div>
);

ThemeSwatch.propTypes = {
  theme: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
};

const InheritedHint = ({ label, url }) => (
  <div className="mt-2 flex items-center gap-2 text-[11px] text-base-content/50">
    {url && (
      <img src={url} alt="" className="w-6 h-6 object-contain rounded border border-base-content/10 bg-white" />
    )}
    <span>Empty — uses the {label}.</span>
  </div>
);

InheritedHint.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string,
};

/**
 * Collapsible branding editor (theme, logo, favicon) shared by the account and
 * sub-account pages. An empty value means "not configured here" and falls back to
 * `inherited` (the parent account's branding for a sub-account, the platform
 * default for an account).
 */
const BrandingSection = ({
  title,
  value,
  onChange,
  uploadLogo,
  uploadFavicon,
  radioName,
  inherited,
}) => {
  const [open, setOpen] = useState(false);
  const source = inherited?.source || 'platform default';
  const defaultTheme = inherited?.theme || 'light';
  const themeOptions = [{ id: '', label: `Default (${defaultTheme})`, swatch: defaultTheme }]
    .concat(DAISY_THEMES.map((theme) => ({ id: theme, label: theme, swatch: theme })));

  return (
    <div className="glass-card rounded-2xl shadow-sm border border-base-content/10 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-5 sm:p-6 bg-transparent hover:bg-base-200/40 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
            <Palette size={16} />
          </div>
          <div>
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {title}
            </h2>
            <p className="text-xs text-base-content/50">
              Configure experience theme, brand logo, and favicon. Empty fields use the {source}.
            </p>
          </div>
        </div>
        <ChevronRight size={18} className={`text-base-content/30 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="p-5 sm:p-6 border-t border-base-content/10 space-y-6 bg-base-200/20">
          {/* Experience Theme */}
          <div>
            <label className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block mb-2">
              Experience Theme
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {themeOptions.map(({ id, label, swatch }) => {
                const isActive = (value.theme || '') === id;
                return (
                  <label
                    key={id || 'default'}
                    title={id ? undefined : `Uses the ${source} theme`}
                    className={`relative flex items-center gap-2 p-1.5 rounded-xl transition-all duration-200 select-none border-2 cursor-pointer ${
                      isActive
                        ? 'border-primary bg-primary/10 shadow-sm'
                        : 'border-base-300 bg-base-100 hover:border-primary/30 hover:bg-base-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name={radioName}
                      value={id}
                      checked={isActive}
                      onChange={() => onChange({ theme: id })}
                      className="sr-only"
                    />
                    <ThemeSwatch theme={swatch} isActive={isActive} />
                    <span className={`text-[11px] font-bold capitalize truncate leading-none pr-1 ${isActive ? 'text-primary' : 'text-base-content/70'}`}>
                      {label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Brand Logo */}
          <div>
            <FileUpload
              id={`${radioName}-logo_url`}
              label="Brand Logo"
              currentUrl={value.logo_url}
              onUploadComplete={(url) => onChange({ logo_url: url })}
              onClear={() => onChange({ logo_url: '' })}
              uploadFn={uploadLogo}
              maxSize={10 * 1024 * 1024}
            />
            {!value.logo_url && <InheritedHint label={`${source} logo`} url={inherited?.logoUrl} />}
          </div>

          {/* Favicon */}
          <div>
            <FileUpload
              id={`${radioName}-favicon_url`}
              label="Favicon"
              currentUrl={value.favicon_url}
              onUploadComplete={(url) => onChange({ favicon_url: url })}
              onClear={() => onChange({ favicon_url: '' })}
              uploadFn={uploadFavicon}
              accept="image/*"
              maxSize={5 * 1024 * 1024}
            />
            {!value.favicon_url && <InheritedHint label={`${source} favicon`} url={inherited?.faviconUrl} />}
          </div>
        </div>
      )}
    </div>
  );
};

BrandingSection.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.shape({
    theme: PropTypes.string,
    logo_url: PropTypes.string,
    favicon_url: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  uploadLogo: PropTypes.func.isRequired,
  uploadFavicon: PropTypes.func.isRequired,
  radioName: PropTypes.string.isRequired,
  /** Branding used for empty fields; `source` names where it comes from (e.g. "account branding") */
  inherited: PropTypes.shape({
    source: PropTypes.string,
    theme: PropTypes.string,
    logoUrl: PropTypes.string,
    faviconUrl: PropTypes.string,
  }),
};

export default BrandingSection;
